import type { DistributionItem, EventRow } from './types';
import { dartRound } from './types';

/** 파생 지표 6. */

/** 집합 맵에 넣기. */
function addTo(m: Map<string, Set<string>>, k: string, v: string): void {
  let s = m.get(k);
  if (s === undefined) { s = new Set(); m.set(k, s); }
  s.add(v);
}

/**
 * 장면 퍼널.
 *
 * ★ 진입한 **회차 수**를 센다(이벤트 수가 아니다 — 한 회차가 같은 장면에 두 번
 * 들어갈 수 있다).
 * ⚠️ 막대 길이는 **첫 장면 대비**다. 각 줄을 100% 로 정규화하면 퍼널이
 *    절대량이 줄어드는 모양을 못 보여준다.
 */
export function lensSceneFunnel(events?: EventRow[] | null): DistributionItem[] {
  const entered = new Map<string, Set<string>>();
  for (const e of events ?? []) {
    if (e.eventName !== 'scene_entered') continue;
    if (e.sceneId === '' || e.runId === '') continue;
    addTo(entered, e.sceneId, e.runId);
  }
  if (entered.size === 0) return [];

  const keys = [...entered.keys()].sort();
  const counts = keys.map((k) => entered.get(k)!.size);
  const top = counts[0]!;

  let dropAt = -1;
  let dropMax = 0;
  for (let i = 1; i < counts.length; i++) {
    const d = counts[i - 1]! - counts[i]!;
    if (d > dropMax) { dropMax = d; dropAt = i; }
  }

  return keys.map((label, i) => {
    // 낙차가 0이면 비운다 — `-0` 은 읽는 사람을 잠깐 멈추게 한다.
    let pctText: string;
    if (i === 0) pctText = '기준';
    else {
      const d = counts[i - 1]! - counts[i]!;
      pctText = d === 0 ? '' : `-${d}`;
    }
    return {
      label,
      count: counts[i]!,
      pct: top === 0 ? 0 : (counts[i]! * 100) / top,
      valueText: `${counts[i]}회차`,
      pctText,
      // 가장 크게 빠진 칸만 빨강.
      tone: i === dropAt ? 'bad' : '',
    };
  });
}

/**
 * 장면별 플래그 획득률 = 플래그를 얻은 회차 / 진입한 회차.
 *
 * ★ **분모가 진입 회차다.** 턴 수로 나누면 오래 머문 장면이 불리해져 비교가
 *   안 된다.
 * ⚠️ 비율을 그리므로 **비율 오름차순으로 정렬**한다 — 건수로 정렬하고 비율을
 *    그리면 막대가 오르내려 고장 난 것처럼 보인다.
 */
export function lensFlagRates(events?: EventRow[] | null): DistributionItem[] {
  const entered = new Map<string, Set<string>>();
  const got = new Map<string, Set<string>>();
  for (const e of events ?? []) {
    const s = e.sceneId;
    if (s === '') continue;
    if (e.eventName === 'scene_entered') {
      addTo(entered, s, e.runId);
    } else if (e.eventName === 'flag_acquired') {
      addTo(got, s, e.runId);
      // 진입 이벤트가 표본 밖으로 잘려도 분모가 0이 되지 않게 한다.
      addTo(entered, s, e.runId);
    }
  }
  const keys = [...entered.keys()].sort((a, b) => {
    const ra = (got.get(a)?.size ?? 0) / entered.get(a)!.size;
    const rb = (got.get(b)?.size ?? 0) / entered.get(b)!.size;
    return ra === rb ? 0 : ra < rb ? -1 : 1;
  });
  return keys.map((k) => {
    const n = entered.get(k)!.size;
    const w = got.get(k)?.size ?? 0;
    const rate = n === 0 ? 0 : (w * 100) / n;
    return { label: k, count: n, pct: rate, valueText: `${w}/${n}`, pctText: `${rate.toFixed(1)}%`, tone: '' };
  });
}

/** 챕터별 **회차당 평균** 원가. */
export function lensCostByChapter(
  events?: EventRow[] | null,
  _costPerTurnKrw?: number | null,
  usdKrwRate?: number | null,
  costFeeRate?: number | null,
): DistributionItem[] {
  const runs = new Map<string, Set<string>>();
  const usd = new Map<string, number>();
  for (const e of events ?? []) {
    const ch = e.chapterId === '' ? '기타' : e.chapterId;
    if (e.costUsd > 0) {
      usd.set(ch, (usd.get(ch) ?? 0) + e.costUsd);
      addTo(runs, ch, e.runId);
    }
    if (e.eventName !== 'scene_completed') continue;
    addTo(runs, ch, e.runId);
  }
  const krwPerUsd = (usdKrwRate ?? 0) * (1 + (costFeeRate ?? 0) / 100);
  const avg = new Map<string, number>();
  for (const ch of runs.keys()) {
    const n = runs.get(ch)?.size ?? 0;
    if (n === 0 || (usd.get(ch) ?? 0) <= 0) continue;
    avg.set(ch, (usd.get(ch)! * krwPerUsd) / n);
  }
  const total = [...avg.values()].reduce((a, b) => a + b, 0);
  return [...avg.keys()].sort().map((k) => {
    const v = avg.get(k)!;
    const pct = total === 0 ? 0 : (v * 100) / total;
    return {
      label: k,
      count: runs.get(k)?.size ?? 0,
      pct,
      valueText: `${dartRound(v)}원`,
      pctText: total === 0 ? '0.0%' : `${pct.toFixed(1)}%`,
      tone: '',
    };
  });
}

/**
 * 응답 시간 요약.
 *
 * ★★ **평균을 일부러 넣지 않는다.** 열에 하나가 20초 걸려도 평균은 거의 안
 * 움직이는데 **그 한 명은 앱을 닫는다.**
 * ★ 정상/거부 갈래를 나누는 이유가 바뀌었다 — 거부 턴도 콜②가 도니 둘은
 *   비슷해야 정상이고, **거부 쪽이 눈에 띄게 빠르면 콜②가 빠진 것**이다
 *   (그때는 오서술률 분모도 함께 틀린 상태다).
 */
export function lensLatencySummary(events?: EventRow[] | null): string {
  const all: number[] = [];
  const normal: number[] = [];
  const rejected: number[] = [];
  for (const e of events ?? []) {
    const ms = e.durationMs;
    if (ms <= 0) continue;
    all.push(ms);
    if (e.eventName === 'unsupported_action_requested') rejected.push(ms);
    else normal.push(ms);
  }
  if (all.length === 0) return '집계할 표본이 없습니다.';

  const pick = (xs: number[], q: number): string => {
    const sorted = [...xs].sort((a, b) => a - b);
    return (sorted[dartRound((sorted.length - 1) * q)]! / 1000).toFixed(1);
  };

  const maxMs = (all.reduce((a, b) => (a > b ? a : b)) / 1000).toFixed(1);
  const parts = [
    `P50 ${pick(all, 0.5)}초`,
    `P95 ${pick(all, 0.95)}초`,
    `최대 ${maxMs}초`,
    `표본 ${all.length}건`,
  ];
  if (rejected.length > 0 && normal.length > 0) {
    parts.push(`정상 턴 P95 ${pick(normal, 0.95)}초`);
    parts.push(`거부 턴 P95 ${pick(rejected, 0.95)}초`);
  }
  return parts.join('  ·  ');
}

/**
 * (장면, 대상)별 막힘. **반복 횟수를 LENS 가 직접 센다.**
 *
 * ★ 분모는 **그 장면에 진입한 회차**다. 거부 건수로 나누면 거부가 많은 장면일수록
 *   비율이 낮아져 **정반대로 읽힌다.**
 * ⚠️ **상위 6곳만 보여준다** — 문턱만 걸면 21줄이 되어 아무것도 지목하지 못한다.
 *    잘라낸 개수는 `lensStuckNotice` 가 배너에서 밝힌다.
 */
export function lensStuckPoints(
  events?: EventRow[] | null,
  floorAttempts?: number | null,
): DistributionItem[] {
  const floor = dartRound(floorAttempts ?? 3);
  const entered = new Map<string, Set<string>>();
  const perRun = new Map<string, Map<string, number>>();
  const sceneOf = new Map<string, string>();
  for (const e of events ?? []) {
    const sc = e.sceneId;
    if (sc === '' || e.runId === '') continue;
    if (e.eventName === 'scene_entered') { addTo(entered, sc, e.runId); continue; }
    if (e.eventName !== 'unsupported_action_requested') continue;
    if (e.propId === '') continue;
    // 라벨 칸이 116px 라 접두사를 뗀다.
    const prop = e.propId.startsWith('p_') ? e.propId.slice(2) : e.propId;
    const key = `${sc}·${prop}`;
    sceneOf.set(key, sc);
    let m = perRun.get(key);
    if (m === undefined) { m = new Map(); perRun.set(key, m); }
    m.set(e.runId, (m.get(e.runId) ?? 0) + 1);
    addTo(entered, sc, e.runId);
  }

  const stuckRuns = new Map<string, number>();
  for (const [key, m] of perRun) {
    let n = 0;
    for (const c of m.values()) if (c >= floor) n++;
    if (n > 0) stuckRuns.set(key, n);
  }

  const rateOf = (k: string): number => {
    const n = entered.get(sceneOf.get(k)!)?.size ?? 0;
    return n === 0 ? 0 : (stuckRuns.get(k)! * 100) / n;
  };

  const keys = [...stuckRuns.keys()].sort((a, b) => {
    const ra = rateOf(a);
    const rb = rateOf(b);
    return ra === rb ? 0 : rb < ra ? -1 : 1;
  });
  // ⚠️ `shown` 은 `lensStuckNotice` 에도 있다 — **같이 고쳐야** 「나머지 N곳」이
  //    맞는다.
  const shown = 6;
  return keys.slice(0, shown).map((k) => {
    const w = stuckRuns.get(k)!;
    const rate = rateOf(k);
    return { label: k, count: w, pct: rate, valueText: `${w}회차`, pctText: `${rate.toFixed(1)}%`, tone: '' };
  });
}

/**
 * 최근 회차 목록.
 *
 * ⚠️ `pctText` 가 「N일 전」이라 **현재 시각에 의존한다.** 정답지와 테스트가
 *    같은 날 안에서 돌면 같은 값이다.
 */
export function lensRecentRuns(
  events?: EventRow[] | null,
  limit?: number | null,
  now = new Date(),
): DistributionItem[] {
  const rows = events ?? [];
  const startedAt = new Map<string, Date>();
  const turns = new Map<string, number>();
  const lastScene = new Map<string, string>();
  const ended = new Set<string>();
  for (const e of rows) {
    const id = e.runId;
    if (id === '') continue;
    const n = e.eventName;
    if (n === 'run_started' && e.occurredAt !== null) startedAt.set(id, e.occurredAt);
    if (n === 'scene_entered' && e.sceneId !== '') lastScene.set(id, e.sceneId);
    if (n === 'ending_reached') ended.add(id);
    if (n === 'action_interpreted' || n === 'unsupported_action_requested' || n === 'input_blocked') {
      turns.set(id, (turns.get(id) ?? 0) + 1);
    }
  }
  if (startedAt.size === 0) return [];

  const ids = [...startedAt.keys()].sort(
    (a, b) => startedAt.get(b)!.getTime() - startedAt.get(a)!.getTime(),
  );
  const take = dartRound(limit ?? 5);
  const top = ids.slice(0, take);

  const ago = (at: Date): string => {
    const ms = now.getTime() - at.getTime();
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return '방금';
    if (minutes < 60) return `${minutes}분 전`;
    const hours = Math.floor(ms / 3600000);
    if (hours < 24) return `${hours}시간 전`;
    return `${Math.floor(ms / 86400000)}일 전`;
  };

  const shortId = (id: string): string => {
    const i = id.indexOf('_');
    const body = i >= 0 ? id.slice(i + 1) : id;
    return body.length <= 8 ? body : body.slice(0, 8);
  };

  return top.map((id) => ({
    label: shortId(id),
    count: turns.get(id) ?? 0,
    pct: 0,
    valueText: `${turns.get(id) ?? 0}턴 · ${lastScene.get(id) ?? '-'}`,
    pctText: ago(startedAt.get(id)!),
    // 완주 여부는 색으로 받는다. 텍스트로 쓰면 줄이 길어진다.
    tone: ended.has(id) ? 'good' : 'warn',
  }));
}
